import React from 'react';
import Button from '../../../../components/ui/Button';
import {
  X,
  DollarSign,
  Calendar,
  AlertCircle,
  CheckCircle,
  User,
  Building,
  CreditCard
} from 'lucide-react';

const DetailField = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-sm text-foreground">{value ?? 'N/A'}</p>
  </div>
);

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleString();
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const parsed = new Date(dateString);
  return Number.isNaN(parsed.getTime()) ? dateString : parsed.toLocaleDateString();
};

const PayrollDetailsModal = ({ isOpen, onClose, payroll, isLoading, error }) => {
  if (!isOpen) return null;

  const payrollData = payroll?.payroll_data || {};
  const allowances = payrollData?.allowances || {};
  const deductions = payrollData?.deductions || {};
  const bankDetails = payrollData?.bank_details || {};

  const statusBadge = (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        payrollData?.status === 'approved' || payrollData?.status === 'paid'
          ? 'bg-green-100 text-green-700'
          : payrollData?.status === 'rejected'
          ? 'bg-red-100 text-red-700'
          : 'bg-yellow-100 text-yellow-700'
      }`}
    >
      {payrollData?.status ? payrollData.status.charAt(0).toUpperCase() + payrollData.status.slice(1) : 'N/A'}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-full">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payroll Details</p>
                <h2 className="text-xl font-semibold text-foreground">
                  {payroll?.employee_name || 'Payroll'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {formatDate(payroll?.pay_period_start)} - {formatDate(payroll?.pay_period_end)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Created {formatDateTime(payroll?.created_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Updated {formatDateTime(payroll?.updated_at)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {statusBadge}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-sm text-muted-foreground">Loading payroll details...</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <>
              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Employee Information
                  </h3>
                  <DetailField label="Employee Name" value={payroll?.employee_name} />
                  <DetailField label="Employee ID" value={payroll?.employee} />
                  <DetailField label="Pay Period Start" value={formatDate(payroll?.pay_period_start)} />
                  <DetailField label="Pay Period End" value={formatDate(payroll?.pay_period_end)} />
                </div>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Salary Summary
                  </h3>
                  <DetailField label="Gross Salary" value={payrollData?.gross_salary ? `₹${Number(payrollData.gross_salary).toLocaleString()}` : 'N/A'} />
                  <DetailField label="Basic Salary" value={payrollData?.basic_salary ? `₹${Number(payrollData.basic_salary).toLocaleString()}` : 'N/A'} />
                  <DetailField label="Net Salary" value={payrollData?.net_salary ? `₹${Number(payrollData.net_salary).toLocaleString()}` : 'N/A'} />
                  <DetailField label="Status" value={payrollData?.status ? payrollData.status.charAt(0).toUpperCase() + payrollData.status.slice(1) : 'N/A'} />
                </div>
              </section>

              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground">Allowances</h3>
                  <DetailField label="House Rent Allowance" value={allowances?.house_rent_allowance ? `₹${Number(allowances.house_rent_allowance).toLocaleString()}` : 'N/A'} />
                  <DetailField label="Transport Allowance" value={allowances?.transport_allowance ? `₹${Number(allowances.transport_allowance).toLocaleString()}` : 'N/A'} />
                  <DetailField label="Medical Allowance" value={allowances?.medical_allowance ? `₹${Number(allowances.medical_allowance).toLocaleString()}` : 'N/A'} />
                  <DetailField 
                    label="Total Allowances" 
                    value={
                      (allowances?.house_rent_allowance || 0) + 
                      (allowances?.transport_allowance || 0) + 
                      (allowances?.medical_allowance || 0) 
                        ? `₹${((allowances?.house_rent_allowance || 0) + (allowances?.transport_allowance || 0) + (allowances?.medical_allowance || 0)).toLocaleString()}`
                        : 'N/A'
                    } 
                  />
                </div>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground">Deductions</h3>
                  <DetailField label="Income Tax" value={deductions?.income_tax ? `₹${Number(deductions.income_tax).toLocaleString()}` : 'N/A'} />
                  <DetailField label="Provident Fund" value={deductions?.provident_fund ? `₹${Number(deductions.provident_fund).toLocaleString()}` : 'N/A'} />
                  <DetailField label="Professional Tax" value={deductions?.professional_tax ? `₹${Number(deductions.professional_tax).toLocaleString()}` : 'N/A'} />
                  <DetailField 
                    label="Total Deductions" 
                    value={
                      (deductions?.income_tax || 0) + 
                      (deductions?.provident_fund || 0) + 
                      (deductions?.professional_tax || 0) 
                        ? `₹${((deductions?.income_tax || 0) + (deductions?.provident_fund || 0) + (deductions?.professional_tax || 0)).toLocaleString()}`
                        : 'N/A'
                    } 
                  />
                </div>
              </section>

              <section className="p-4 border border-gray-200 rounded-lg space-y-4">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Payment Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DetailField label="Payment Method" value={payrollData?.payment_method ? payrollData.payment_method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'} />
                  <DetailField label="Account Number" value={bankDetails?.account_number} />
                  <DetailField label="IFSC Code" value={bankDetails?.ifsc_code} />
                </div>
              </section>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PayrollDetailsModal;

